import React, { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Lead } from 'services/leads';
import styled from 'styled-components';
import { Stack, Typography } from '@mui/material';
import { StatusFiles } from 'const/option';
import { AddNote } from '../../../pages/FileDetail/components/LeadBox';
import { ButtonCustom } from '../ButtonCustom';
import { widthScreen } from 'const/common';

type ModalDetailLeadProps = {
  itemMVS?: Lead;
  isOpen?: Lead;
  onClose: () => void;
  handleAddNewNote: (value: AddNote) => void;
  handleEditNote: (idLead: number, idNote: number, content: string) => void;
  handleDeleteNoteLead: (idNote: number, idLead: number) => void;
  navigateDetail: () => void;
};
const ModalDetailLead = ({ isOpen, onClose, navigateDetail, itemMVS }: ModalDetailLeadProps) => {
  const div = useRef<HTMLDivElement | null>(null);
  const portElement = useMemo(() => {
    if (!div.current) {
      div.current = document.createElement('div');
      document.body.append(div.current);
    }
    return div.current;
  }, []);
  useEffect(() => () => portElement.remove(), []);
  if (!portElement) return null;
  const findStatus = StatusFiles.find((item) => item.value === isOpen?.file?.status);
  const mvsLead = isOpen?.file?.leads?.find((l) => l.type === 'MVS') ?? itemMVS;

  return createPortal(
    <Container $isOpen={isOpen}>
      {isOpen && <Overlay onClick={onClose} />}
      <Content style={{ maxWidth: widthScreen < 450 ? 'calc(100% - 30px)' : '450px' }}>
        <Stack
          style={{
            background: findStatus?.color || '#9747FF',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0px 10px',
            height: '20px',
            borderRadius: '5px',
            width: 'max-content'
          }}>
          <Typography
            style={{
              color: '#fff',
              fontSize: '10px',
              fontWeight: 600
            }}>
            {[
              findStatus?.value?.charAt(0)?.toUpperCase() ?? '',
              findStatus?.value?.slice(1)?.toLocaleLowerCase() ?? ''
            ].join('')}
          </Typography>
        </Stack>
        <ListItem>
          <Item>
            <ItemHalfWidth>
              <Label>Year, Make, Model: </Label>
              <Value style={{ fontWeight: '700' }}>
                {[
                  isOpen?.file?.year ?? '',
                  isOpen?.file?.make ?? '',
                  isOpen?.file?.model ?? ''
                ].join(' ')}
              </Value>
            </ItemHalfWidth>
            <ItemHalfWidth>
              <Label>Debtor: </Label>
              <Value>{isOpen?.file?.lastName + ' ' + isOpen?.file?.firstName}</Value>
            </ItemHalfWidth>
          </Item>
          <Item>
            <ItemHalfWidth>
              <Label>Colour: </Label>
              <Value style={{ fontWeight: '700' }}>
                {mvsLead?.colour || isOpen?.file?.colour || ''}
              </Value>
            </ItemHalfWidth>
            <ItemHalfWidth>
              <Label>CoBo: </Label>
              <Value>
                {(isOpen?.file?.coBorrowerFirstName || '') +
                  ' ' +
                  (isOpen?.file?.coBorrowerLastName || '')}
              </Value>
            </ItemHalfWidth>
          </Item>
          <Item>
            <ItemHalfWidth>
              <Label>Plate: </Label>
              <Value style={{ fontWeight: '700' }}>
                {mvsLead?.plate || isOpen?.file?.plate || ''}
              </Value>
            </ItemHalfWidth>
            <ItemHalfWidth>
              <Label>Address: </Label>
              <Value>{`${isOpen?.address || ''}`}</Value>
            </ItemHalfWidth>
          </Item>
          <Item>
            <ItemHalfWidth>
              <Label>VIN Check: </Label>
              <Value>{isOpen?.file?.vin}</Value>
            </ItemHalfWidth>
            <ItemHalfWidth>
              <Label>Auction House: </Label>
              <Value>{isOpen?.file?.auctionDeliveryLocation?.value || ''}</Value>
            </ItemHalfWidth>
          </Item>
        </ListItem>
        <Stack style={{ width: '100%', alignItems: 'flex-end' }}>
          <ButtonCustom
            style={{ width: 120, height: 40, marginTop: 16 }}
            onClick={navigateDetail}
            label={'View Detail'}
          />
        </Stack>
      </Content>
    </Container>,
    portElement
  );
};

export default React.memo(ModalDetailLead);

const Container = styled.div<{ $isOpen?: Lead }>`
  height: 100dvh;
  background: transparent;
  position: fixed;
  transition: all 0.15s linear;
  top: ${({ $isOpen }) => ($isOpen ? '0%' : '-100%')};
  left: 0;
  z-index: 99999;
  width: 100vw;
  display: flex;
`;

const Overlay = styled.div`
  width: 100vw;
  height: 100dvh;
  position: absolute;
  z-index: 999;
  top: 0;
  left: 0;
  cursor: pointer;
`;

const Content = styled.div`
  width: 100%;
  background: white;
  border: 3px solid rgba(34, 55, 74, 1);
  border-radius: 20px;
  margin: auto;
  position: relative;
  z-index: 9999;
  padding: 15px 25px 20px 25px;
`;

const Item = styled.div`
  display: flex;
  align-items: flex-start;
`;

const ListItem = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  margin-top: 10px;
`;

const ItemHalfWidth = styled.div`
  width: 50%;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;
const Label = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #686e7d;
`;

const Value = styled.span`
  color: #22374a;
  font-size: 14px;
  font-weight: 500;
`;
